import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FlowRow {
  readonly flowId: string;
  readonly flowCode: string;
  readonly flowName: string;
  readonly integrationType: string;
  readonly status: string;
  readonly lastRunStatus: string | null;
  readonly lastRunAt: string | null;
}

const TYPES = ['', 'REAL_TIME', 'BATCH', 'STREAMING', 'SCHEDULED'];
const STATUSES = ['', 'DRAFT', 'ACTIVE', 'PAUSED', 'RETIRED'];

/**
 * GR L29.01 — ESB Integration Flows (`/admin/esb/flows`). Catalogue of
 * `esb integration flows` with their integration type, lifecycle status and
 * last-run outcome, each opening the visual designer. Filterable by type and
 * status.
 */
export function GrEsbFlowsPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FlowRow[]>([]);
  const [integrationType, setIntegrationType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (integrationType) params.set('integrationType', integrationType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FlowRow[] }>(`/v1/eip/flows${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, integrationType, status]);

  return (
    <main aria-label="esb-flows">
      <h1>{tr('gr29.flows.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr29.flows.labelType')}
          <select aria-label="filter-type" value={integrationType} onChange={(e) => setIntegrationType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr29.flows.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr29.flows.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr29.flows.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr29.flows.loading')}</p> : (
        <>
          <p aria-label="flow-count">{tr('gr29.flows.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr29.flows.empty')}</p> : (
            <table aria-label="flow-table">
              <thead><tr><th>{tr('gr29.flows.colCode')}</th><th>{tr('gr29.flows.colName')}</th><th>{tr('gr29.flows.colType')}</th><th>{tr('gr29.flows.colStatus')}</th><th>{tr('gr29.flows.colLastRun')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.flowId}>
                    <td>{f.flowCode}</td><td>{f.flowName}</td><td>{f.integrationType}</td>
                    <td>{f.status}</td><td>{f.lastRunStatus ?? '—'}</td>
                    <td><Link aria-label={`open-${f.flowId}`} to={`/admin/esb/flows/${f.flowId}`}>{tr('gr29.flows.designer')}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
